import { css } from '@emotion/react'
import { Breadcrumb, Button, Layout, notification } from 'antd'
import { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, Prompt, useHistory, useLocation } from 'react-router-dom'
import { Header } from 'src/components/header/Header'
import Loading from 'src/components/loading/Loading'
import LoadingOverlay from 'src/components/loading/LoadingOverlay'
import { localStorageKeys } from 'src/constants/localStorageKeys'
import DataUnavailableWidget from 'src/features/dataUnavailableWidget/DataUnavailableWidget'
import { useDataUnavailableWidget } from 'src/features/dataUnavailableWidget/useDataUnavailableWidget'
import { showNotification } from 'src/features/notificationService/slice'
import {
  buildCreateTemplateV3Body,
  buildUpdateTemplateV3Body,
} from 'src/features/templateEdit/api/buildTemplateV3Body'
import { useMutationCreateTemplateV3 } from 'src/features/templateEdit/api/useMutationCreateTemplateV3'
import { useMutationUpdateTemplateV3 } from 'src/features/templateEdit/api/useMutationUpdateTemplateV3'
import { extractUniqueMultipleChoiceSets } from 'src/features/templateEdit/extractUniqueMultipleChoiceSets'
import EditGridTemplateOption from 'src/features/templateEdit/templateOptions/EditGridTemplateOption'
import interceptionsActions from 'src/state/ducks/interceptions/actions'
import { getMultipleChoiceSets } from 'src/state/ducks/multipleChoiceSets/actions'
import {
  addSuccessTemplatePages,
  getTemplatePages,
  resetTemplatePages,
  setTemplatePageErrorMessage,
} from 'src/state/ducks/templates/actions'
import {
  addErrorTemplate,
  addSuccessTemplate,
  getEmptyGridTemplate,
  getPreparedTemplate,
  getTemplate,
  resetTemplate,
  setTemplateNameErrorMessage,
  updateErrorTemplate,
  updateSuccessTemplate,
} from 'src/state/ducks/templates/editTemplate/actions'
import {
  addSuccessTemplateHints,
  resetTemplateHints,
} from 'src/state/ducks/templates/templateHints/actions'
import { getUserGroups } from 'src/state/ducks/users/actions'
import { RootState } from 'src/state/store'
import { ExhaustiveError } from 'src/util/error'
import { getBoolParamValue } from 'src/util/urlSearchParamsUtils'
import { HistoryState } from 'src/views/components/routes/getUserConfirmation'
import {
  TemplateEditModeProvider,
  useTemplateEditMode,
} from './context/TemplateEditMode/TemplateEditMode'
import { GridTemplateLabel, LayoutIcon } from './EditGridTemplate.dumb'
import { SoftLimitErrorDialogForGrid } from './errorModal/SoftLimitErrorDialogForGrid'
import EditGridTemplatePages from './templatePages/EditGridTemplatePages'
import { useGridLayoutSoftLimitErrorLog } from './useSoftLimitErrorLog'
import { validateGridTemplatePage } from './validates/validate'

const EditGridTemplateContainer: FC = () => {
  return (
    <TemplateEditModeProvider>
      <EditGridTemplateInner />
    </TemplateEditModeProvider>
  )
}

const EditGridTemplateInner: FC = () => {
  const dispatch = useDispatch()

  const companyId = localStorage.getItem(localStorageKeys.loginCompanyId) ?? ''
  const {
    editTemplate: templateData, // ひな形関係
    templatePages: templatePagesData, // ひな形ページ関係
    templateHints: templateHintsData, // ひな形ヒント関係 (未利用だけどなぜか読み書きしている)
  } = useSelector((state: RootState) => state.templatesState)

  const isDirty = templateData.isDirty || templatePagesData.isDirty

  const createMutation = useMutationCreateTemplateV3()
  const updateMutation = useMutationUpdateTemplateV3()
  const isSaving = createMutation.isPending || updateMutation.isPending

  const templateEditMode = useTemplateEditMode()

  const history = useHistory()

  // 新規作成成功時に一覧画面へ遷移する。
  // onSuccess 内で直接 history.push すると、React 再レンダリング前に Prompt がブロックするため、
  // useEffect で createMutation.isSuccess を監視して遷移する。
  useEffect(() => {
    if (createMutation.isSuccess) {
      history.push('/templates')
    }
  }, [createMutation.isSuccess, history])

  const location = useLocation()
  const hasVariablesParam = getBoolParamValue(location.search, 'hasVariables')

  const dataUnavailableWidget = useDataUnavailableWidget()

  useEffect(() => {
    switch (templateEditMode.mode) {
      case 'new': {
        dispatch(getEmptyGridTemplate(hasVariablesParam))
        break
      }
      case 'newFromPreparedTemplate': {
        // 表形式のテンプレートは現在は未利用だが、今後活用する可能性が少しだけある
        // https://kaminashi-inc.slack.com/archives/C047X5HBRK5/p1705995368660799
        dispatch(
          getPreparedTemplate(
            templateEditMode.templateId,
            templateEditMode.key,
          ),
        )
        break
      }
      case 'edit': {
        dispatch(getTemplatePages(templateEditMode.templateId))
        dispatch(getTemplate(templateEditMode.templateId))
        break
      }
    }
    dispatch(getUserGroups({}))
    dispatch(getMultipleChoiceSets({}))

    return () => {
      dispatch(resetTemplate())
      dispatch(resetTemplatePages())
      dispatch(resetTemplateHints())
      dataUnavailableWidget.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const overlayTip =
    templateEditMode.mode === 'edit'
      ? 'ひな形を更新中です...'
      : 'ひな形を登録中です...'

  const isInitialLoading =
    templateHintsData.isLoading || templatePagesData.isLoading

  const { logSoftLimitErrorEvent } =
    useGridLayoutSoftLimitErrorLog('updateTemplate')

  const saveTemplate = () => {
    // ひな形名のエラーと、page 配下のエラーは別々に出せるので、両方のエラーを同時に出せるように分けて処理している
    let isInvalid = false
    if (templateData.name === '') {
      dispatch(setTemplateNameErrorMessage('ひな形名を入力してください'))
      isInvalid = true
    }

    const [isValid, errMessage, gridSoftLimitErrorType] =
      validateGridTemplatePage(
        templatePagesData.templateNodes,
        templatePagesData.templatePages,
        companyId,
      )
    if (!isValid) {
      dispatch(setTemplatePageErrorMessage(errMessage))
      isInvalid = true
    }

    // !isValid の場合しか、ソフトリミットのエラーも存在しないが、
    // !isValid の分岐内に書く意味も特にないので、この階層におく
    if (gridSoftLimitErrorType) {
      logSoftLimitErrorEvent(gridSoftLimitErrorType, errMessage)
    }

    if (isInvalid) {
      notification.error({
        message: 'ひな形の保存に失敗しました。',
        description: 'エラー箇所を確認して修正してください。',
        duration: 2,
      })

      setTimeout(() => {
        const errorElement = document.getElementById('error-message')
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)

      return
    }

    const multipleChoiceSets = extractUniqueMultipleChoiceSets(
      templatePagesData.templateNodes,
    )
    const templateArgs = {
      templatePages: templatePagesData.templatePages,
      templateNodes: templatePagesData.templateNodes,
      templatePageIds: templatePagesData.templatePageIds,
      templateHints: templateHintsData.templateHints,
      name: templateData.name,
      isShowScore: templateData.isShowScore,
      isEditable: templateData.isEditable,
      isExcelConversion: templateData.isExcelConversion,
      isHideQuestionOptions: templateData.isHideQuestionOptions,
      isAudit: templateData.isAudit,
      isKeyboard: templateData.isKeyboard,
      layoutType: templateData.layoutType,
      hasVariables: templateData.hasVariables,
      manuals: templateData.manuals,
      icons: templateData.icons,
      approvalFlowId: templateData.approvalFlowId,
      placeNodes: templateData.selectedPlaceNodes,
      multipleChoiceSets,
      templateReportNamingRulesTemplateNodeUuid:
        templatePagesData.templateReportNamingRulesTemplateNodeUuid,
      masterUuid: templateData.masterUuid,
    }
    switch (templateEditMode.mode) {
      case 'edit':
        updateMutation.mutate(
          {
            templateId: templateEditMode.templateId,
            body: buildUpdateTemplateV3Body(templateArgs),
          },
          {
            onSuccess: () => {
              dispatch(updateSuccessTemplate())
              dispatch(addSuccessTemplatePages())
              dispatch(addSuccessTemplateHints())
              dispatch(setTemplatePageErrorMessage(''))
              dispatch(
                showNotification({ message: 'ひな形の更新に成功しました' }),
              )
            },
            onError: error => {
              dispatch(interceptionsActions.handleHttpError(error))
              dispatch(updateErrorTemplate(error))
            },
          },
        )
        break
      case 'new':
      case 'newFromPreparedTemplate':
        createMutation.mutate(buildCreateTemplateV3Body(templateArgs), {
          onSuccess: _data => {
            // _data.id で作成されたひな形の ID を参照可能（将来の拡張用）
            dispatch(addSuccessTemplate())
            dispatch(addSuccessTemplatePages())
            dispatch(addSuccessTemplateHints())
            dispatch(
              showNotification({ message: 'ひな形の作成に成功しました' }),
            )
            // 遷移は useEffect で createMutation.isSuccess を監視して行う
          },
          onError: error => {
            dispatch(interceptionsActions.handleHttpError(error))
            dispatch(addErrorTemplate(error))
          },
        })
        break
      default:
        throw new ExhaustiveError(templateEditMode)
    }
  }

  const [historyState, setHistoryState] = useState<HistoryState>()
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
    setHistoryState({
      data: window.history.state,
      title: document.title,
      url: window.location.href,
    })
  }, [])

  return (
    <>
      <SoftLimitErrorDialogForGrid />
      <LoadingOverlay
        tip={overlayTip}
        spinning={isSaving}
        size="large"
        render={
          <>
            <Header
              rightSlot={[
                <Button
                  key="1"
                  type="primary"
                  onClick={() => {
                    saveTemplate()
                  }}
                  disabled={dataUnavailableWidget.isVisible}
                >
                  保存
                </Button>,
              ]}
              sticky
            >
              <Breadcrumb css={styles.breadcrumb}>
                <Breadcrumb.Item>
                  <Link to="/templates">
                    <LayoutIcon />
                    ひな形一覧
                  </Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                  ひな形{templateEditMode.mode === 'edit' ? 'の変更' : 'の追加'}{' '}
                  <GridTemplateLabel>表形式</GridTemplateLabel>
                </Breadcrumb.Item>
              </Breadcrumb>
            </Header>
            {dataUnavailableWidget.isVisible ? (
              <DataUnavailableWidget />
            ) : (
              <Layout className="layout">
                <Prompt
                  when={isDirty && !createMutation.isSuccess}
                  message={location => {
                    const message =
                      '変更した内容が破棄されますがよろしいですか？'
                    // NOTE: キャンセルしたときにURLがもとに戻らないバグがある
                    if (location.pathname === '/templates') {
                      return JSON.stringify({ message, state: historyState })
                    }
                    return message
                  }}
                />
                <Layout.Content>
                  {isInitialLoading ? (
                    <Loading />
                  ) : (
                    <>
                      <EditGridTemplateOption />
                      <EditGridTemplatePages />
                    </>
                  )}
                </Layout.Content>
              </Layout>
            )}
          </>
        }
      />
    </>
  )
}

const styles = {
  breadcrumb: css`
    font-weight: bold;
  `,
}

export default EditGridTemplateContainer
